import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Check, Download, Link2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { getConfig, qrDownloadUrl } from "../lib/api";

export const QRStudioCard = ({ config, loading, qrId = "main" }) => {
  const [copied, setCopied] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fixedLink = config?.fixed_link || "";

  const triggerPulse = () => {
    setPulse(true);
    setTimeout(() => setPulse(false), 450);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fixedLink);
      setCopied(true);
      triggerPulse();
      toast.success("Link copiado para a área de transferência!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      triggerPulse();
      const res = await getConfig(qrId); // warm-up / ensure backend reachable
      void res;
      const a = document.createElement("a");
      a.href = qrDownloadUrl(qrId);
      a.download = `qr-iudp-${qrId}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Baixando QR Code em alta resolução…");
    } catch {
      toast.error("Falha ao baixar o QR Code.");
    } finally {
      setTimeout(() => setDownloading(false), 800);
    }
  };

  return (
    <motion.div
      data-testid="qr-studio-card"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="rounded-3xl border border-border bg-card p-6 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.25)] sm:p-8"
    >
      <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
        <ShieldCheck className="h-3.5 w-3.5" />
        Permanente • nunca muda
      </div>

      <div className="flex flex-col items-center gap-6">
        <div
          className={`rounded-2xl border-4 border-slate-900/5 bg-white p-4 shadow-inner dark:border-white/10 ${
            pulse ? "qr-pulse" : ""
          }`}
        >
          {loading || !config ? (
            <div className="h-56 w-56 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />
          ) : (
            <img
              data-testid="qr-code-image"
              src={config.qr_png_base64}
              alt="QR Code permanente"
              className="h-56 w-56"
            />
          )}
        </div>

        <div className="w-full">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <Link2 className="h-3.5 w-3.5" /> Link público fixo
          </p>
          <div
            data-testid="fixed-public-link-display"
            className="w-full break-all rounded-xl border border-border bg-secondary px-4 py-3 font-mono text-sm text-foreground"
          >
            {fixedLink || "…"}
          </div>
        </div>

        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
          <Button
            data-testid="copy-fixed-link-button"
            variant="outline"
            size="lg"
            onClick={handleCopy}
            disabled={!fixedLink}
            className="h-12 rounded-xl text-base transition-transform active:scale-[0.98]"
          >
            {copied ? (
              <Check className="mr-2 h-5 w-5 text-emerald-500" />
            ) : (
              <Copy className="mr-2 h-5 w-5" />
            )}
            {copied ? "Copiado!" : "Copiar link"}
          </Button>

          <Button
            data-testid="download-qr-png-button"
            size="lg"
            onClick={handleDownload}
            disabled={downloading || !fixedLink}
            className="h-12 rounded-xl bg-primary text-base text-primary-foreground transition-transform hover:bg-primary/90 active:scale-[0.98]"
          >
            <Download className="mr-2 h-5 w-5" />
            {downloading ? "Baixando…" : "Baixar QR Code"}
          </Button>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Imprima este QR Code onde quiser. Ao escanear, ele sempre abre a mesma
          página da plataforma — você troca o vídeo quando quiser, sem reimprimir.
        </p>
      </div>
    </motion.div>
  );
};
